<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router'
import type { SupersizingRedirectModalConfiguration } from '~/types/supersizing'
import { SEARCH_SELECT_ITEM_CATEGORY_PARAM, SEARCH_SELECT_ITEM_EVENT, SEARCH_SELECT_ITEM_ID_PARAM } from '~/utils/analytics/search'

definePageMeta({
  layout: 'empty',
  middleware: [
    'force-page-one-if-empty',
    (to) => {
      const { mustRedirect, nextQuery } = sanitizeSearchQuery(to.query)
      if (mustRedirect) {
        return navigateTo({ name: 's', query: nextQuery })
      }
    },
  ],
})

const route = useRoute()
const { t } = useI18n()

const { isLoggedIn } = storeToRefs(useUserStore())
const { results, filters, markedItemId, markedItem: mapMarkedItem, renderMap, showQuickSearch, isCrossedResults } = storeToRefs(useSearchStore())
const { getReferentialOptions } = useReferentialStore()

const { parseSearchQuery } = useParseSearchQuery()

watch(() => route.query, (value) => {
  filters.value = parseSearchQuery(value)
}, {
  deep: true,
  immediate: true,
})

onBeforeRouteLeave((to, _from, next) => {
  if (to.name === 'campers-id') {
    const { id: _id } = to.params
    const _idAsInt = Array.isArray(_id) ? parseInt(_id[0]) : parseInt(_id)
    const vehicle = results.value.find(({ id }) => id === _idAsInt)

    if (vehicle) {
      const { event } = useGtag()
      event(SEARCH_SELECT_ITEM_EVENT, {
        [SEARCH_SELECT_ITEM_ID_PARAM]: _id,
        [SEARCH_SELECT_ITEM_CATEGORY_PARAM]: vehicle.vehicle_type,
      })
    }
  }
  next()
})

const resetMarkedItem = () => {
  markedItemId.value = null
}

const wishlistProductId = ref<number | null>(null)

useSeoDefault()
useSeoImage()

const {
  public: {
    meta: {
      vehicleCount,
    },
  },
} = useRuntimeConfig()

const seoTitle = computed(() => {
  const types = queryValueToArray(route.query.types)
  if (types.length === 1) {
    const types_string = getReferentialOptions('vehicle_type_choices').find(({ value }) => value === +types[0])?.text
    if (types_string) {
      return t('pages.s.meta.title_with_types_dynamic', { types_string })
    }
  }
  return ''
})
useSeoText({ title: seoTitle, description: t('pages.s.meta.description_dynamic', { count_string: vehicleCount }) })

// Only index `page` and `type` parameters
const params = [
  { key: 'types', sort: (arr: string[]) => arr.sort((a, b) => parseInt(a) - parseInt(b)) },
  { key: 'page' },
]
useHeadCanonical({ params })
useHeadAlternateLinks({ params })

const supersizing = ref({
  show: route.query.utm_source?.includes('goboony') || false,
  modal: {
    show: false,
  },
})

const toggleSupersizingModal = (open: boolean) => {
  supersizing.value.modal.show = open
}

const { loggedIn } = storeToRefs(useUserStore())

callOnce(() => {
  if (queryValueToBoolean(route.query.hide_map_initial)) {
    renderMap.value = false
  }
  else if (route.query.longitude && route.query.latitude && route.query.radius) {
    renderMap.value = true
  }
})

const supersizingRedirect: Ref<SupersizingRedirectModalConfiguration> = ref({
  callbacks: {
    redirectUrl: undefined,
    keepNavigateAction: undefined,
  },
  modal: {
    show: false,
    blocking: false,
    country: undefined,
  },
})

const onOpenSupersizingRedirectModal = ({ query, redirect: redirectUrl, isBlocking, countryCode }: { query: () => void, redirect: RouteLocationRaw, isBlocking: boolean, countryCode: string }) => {
  if (!isBlocking) {
    query()
    return
  }
  supersizingRedirect.value.modal.show = true
  supersizingRedirect.value.modal.blocking = isBlocking
  supersizingRedirect.value.modal.country = countryCode
  supersizingRedirect.value.callbacks = {
    redirectUrl,
    keepNavigateAction: query,
  }
}

const onCloseSupersizingRedirectModal = () => {
  supersizingRedirect.value.modal.show = false
  if (supersizingRedirect.value.callbacks.keepNavigateAction) {
    supersizingRedirect.value.callbacks.keepNavigateAction()
  }
}
</script>

<template>
  <div>
    <div class="min-h-screen">
      <div class="sticky top-0 z-[40]">
        <!-- Supersizing banner -->
        <AppSearchSupersizingBanner
          v-if="supersizing.show"
          class="container-search"
          @close="supersizing.show = false"
          @read-more="toggleSupersizingModal(true)"
        />
        <AppSupersizingAboutModal
          v-if="supersizing.modal.show"
          @close="toggleSupersizingModal(false)"
        />
        <AppSupersizingRedirectModal
          v-if="supersizingRedirect.modal.show"
          :redirect-url="supersizingRedirect.callbacks.redirectUrl"
          :country="supersizingRedirect.modal.country"
          :blocking="true"
          @close="onCloseSupersizingRedirectModal"
        />

        <AppHeaderContainer
          class="pb-4 pt-2.5 md:py-4"
          container="container-search"
        >
          <template #center>
            <div class="md:hidden flex bg-gray-50 border-y border-gray-300 -mx-4 px-4 py-2">
              <HoSearchBar
                is-light
                class="mr-2 w-full"
                @open-supersizing-modal="onOpenSupersizingRedirectModal"
              />
              <AppSearchFiltersButton />
            </div>

            <div class="hidden md:block rounded border border-gray-200">
              <HoSearchBar
                is-light
                @open-supersizing-modal="onOpenSupersizingRedirectModal"
              />
            </div>
          </template>

          <template #end>
            <AppHeaderMenu />
          </template>
        </AppHeaderContainer>

        <HoSearchFilters class="hidden md:block" />
      </div>

      <main>
        <div>
          <HoSearchResults @wishlist="wishlistProductId = $event" />
          <YscModal
            v-if="mapMarkedItem"
            class="!z-[80] flex md:hidden"
            :scroll-lock="false"
            @close="resetMarkedItem"
          >
            <AppSearchResultsCamperCard
              v-if="mapMarkedItem"
              :camper="mapMarkedItem"
              :selected-date-from="filters.date_from"
              :selected-date-to="filters.date_to"
              :show-wishlist-button="isLoggedIn"
              @wishlist-click="wishlistProductId = $event"
            />
          </YscModal>

          <AppModalCamperWishlist
            v-if="wishlistProductId && loggedIn"
            :id-camper="wishlistProductId"
            @close="wishlistProductId = null"
          />
        </div>
      </main>
    </div>

    <AppFooter
      class="hidden lg:block"
      container="container-search"
    />

    <ClientOnly v-if="!useFeatureFlag('hideHelp')">
      <AppHelp />
    </ClientOnly>
    <AppModalSearchQuickSearch
      v-if="showQuickSearch"
      :is-crossed-search="isCrossedResults"
      class="z-[90]"
      @close="showQuickSearch = false"
    />
  </div>
</template>
